import { Button, Grid, Loader, SelectField } from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { CTable } from "@coreui/react";
import AutorenewIcon from "@mui/icons-material/Autorenew";

import {
  getDashboardStandard,
  listBranches,
  listCompanies,
  listVariables,
  userPreferencesByUserID,
} from "graphql/queries";
import { LayoutContext } from "layout/context/LayoutContext";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";
import { createUserPreference, updateUserPreference } from "graphql/mutations";
import StandardFilterCard from "components/dashboards/StandardFilterCard";
import { formatNumber } from "components/utils/functions";

const DashboardStandard = () => {
  const { me } = useContext(LayoutContext);
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
    branch: {
      id: "",
      display: "",
    },
  });
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [dashboard, setDashboard] = useState({
    list: [],
    loading: false,
  });
  const [variables, setVariables] = useState({
    list: [],
    loading: false,
  });
  const [filterBy, setFilterBy] = useState({
    companies: [],
    branches: [],
    variables: [],
    time: "",
    isLoaded: false,
  });
  const tableColumns = useMemo(() => {
    let cols = [
      {
        key: "device",
        label: strings.table.headers.device,
        _style: { width: "20%" },
      },
      {
        key: "company",
        label: strings.table.headers.company,
        _style: { width: "20%" },
      },
      {
        key: "branch",
        label: strings.table.headers.branch,
        _style: { width: "20%" },
      },
    ];

    if (filterBy.companies?.length === 1)
      cols = cols.filter((col) => col.key !== "company");
    if (filterBy.branches?.length === 1)
      cols = cols.filter((col) => col.key !== "branch");

    filterBy.variables?.forEach((variable) => {
      if (variables.list?.length <= 0) return;
      const varFound = variables.list?.find(
        (prevVar) => prevVar.shortName === variable.shortName
      );
      cols.push({
        key: `var_${varFound.shortName}`,
        label: `${varFound.name} (${varFound.unit})`,
        _style: { width: "10%" },
        _props: { className: "text-end" },
      });
    });
    return cols;
  }, [filterBy, variables.list]);

  const onAdd = async (name, value) => {
    let finalObj = {};
    if (!value) return;

    if (name === "variables") {
      if (filterBy.variables?.some((fil) => fil.shortName === value)) return;
      finalObj = getVariablesByShortnames([value])[0];
    }
    if (name === "branches") {
      if (filterBy.branches?.some((fil) => fil.id === value)) return;
      finalObj = (await getBranchesByIds([value]))[0];
      onAdd("companies", finalObj.companyID);
    }
    if (name === "companies") {
      if (filterBy.companies?.some((fil) => fil.id === value)) return;
      finalObj = (await getCompaniesByIds([value]))[0];
    }
    setFilterBy((prev) => ({
      ...prev,
      [name]: [...prev[name], finalObj],
    }));
  };

  const onRemove = (name, value) => {
    if (name === "variables") {
      setFilterBy((prev) => ({
        ...prev,
        variables: prev.variables?.filter((prevVar) => prevVar.id !== value),
      }));
    }
    if (name === "branches") {
      setFilterBy((prev) => ({
        ...prev,
        branches: prev.branches?.filter((prevBr) => prevBr.id !== value),
      }));
    }
    if (name === "companies") {
      setFilterBy((prev) => ({
        ...prev,
        companies: prev.companies?.filter((prevCom) => prevCom.id !== value),
        branches: prev.branches?.filter((prevBr) => prevBr.companyID !== value),
      }));
    }
  };

  const onSelectTime = (time) => {
    setFilterBy((prev) => ({
      ...prev,
      time,
    }));
  };

  const getVariablesByShortnames = (names) =>
    variables.list?.filter((varLs) => names?.includes(varLs.shortName)) ?? [];

  const getBranchesByIds = async (ids, skipLoading) => {
    try {
      if (!skipLoading) setBranchesLoading(true);
      const branchesData = await API.graphql(
        graphqlOperation(listBranches, {
          filter: { or: ids.map((id) => ({ id: { eq: id } })) },
        })
      );
      return branchesData.data.listBranches.items;
    } catch (err) {
      toast.error(strings.responses.errorFetchBranches);
      return [];
    } finally {
      if (!skipLoading) setBranchesLoading(false);
    }
  };

  const getCompaniesByIds = async (ids) => {
    try {
      setCompaniesLoading(true);
      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          filter: { or: ids.map((id) => ({ id: { eq: id } })) },
        })
      );
      return companiesData.data.listCompanies.items;
    } catch (err) {
      toast.error(strings.responses.errorFetchCompanies);
      return [];
    } finally {
      setCompaniesLoading(false);
    }
  };

  async function fetchVariables() {
    try {
      setVariables((prev) => ({ ...prev, loading: true, list: [] }));

      const variablesData = await API.graphql(graphqlOperation(listVariables));
      const variablesItems = variablesData.data.listVariables.items;
      setVariables((prev) => ({
        ...prev,
        list: variablesItems,
      }));
      return variablesItems;
    } catch (err) {
      toast.error(strings.responses.errorFetchVariables);
    } finally {
      setVariables((prev) => ({ ...prev, loading: false }));
    }
  }

  async function fetchDashboard() {
    try {
      if (
        !filterBy.time ||
        filterBy.variables?.length <= 0 ||
        filterBy.companies?.length <= 0
      ) {
        setDashboard({ loading: false, list: [] });
        return;
      }

      setDashboard((prev) => ({ ...prev, loading: true }));

      const dashboardDetails = await API.graphql(
        graphqlOperation(getDashboardStandard, {
          companies: filterBy.companies?.map((comp) => comp.id) ?? [],
          branches: filterBy.branches?.map((br) => br.id) ?? [],
          variables:
            filterBy.variables?.map((variable) => variable.shortName) ?? [],
          time: filterBy.time,
        })
      );
      const dashboardRes = dashboardDetails.data.getDashboardStandard;

      if (!dashboardRes?.success) {
        toast.error(dashboardRes?.error);
      } else {
        const branchesFound = await getBranchesByIds(
          dashboardRes.data?.map((d) => d.branchID),
          true
        );
        const dashboardData = dashboardRes.data?.map((data) => {
          let varObjs =
            filterBy.variables?.reduce(
              (a, v) => ({ ...a, [`var_${v.shortName}`]: "" }),
              {}
            ) ?? {};
          data.variables?.forEach(
            (dataVar) =>
              (varObjs[`var_${dataVar.name}`] =
                variables.list?.find(
                  (varLi) => varLi.shortName === dataVar.name
                )?.type === "onOff" && filterBy.time === "lastValue"
                  ? Number(dataVar.value) === 0
                    ? "Off"
                    : "On"
                  : formatNumber(dataVar.value, 2))
          );
          return {
            device: data.deviceName,
            branch:
              branchesFound?.find((br) => br.id === data.branchID)?.name ?? "",
            company:
              filterBy.companies?.find((br) => br.id === data.companyID)
                ?.name ?? "",
            ...varObjs,
            _cellProps:
              Object.entries(varObjs)?.reduce(
                (a, b) => ({ ...a, [b[0]]: { className: "text-end" } }),
                {}
              ) ?? {},
          };
        });

        setDashboard((prev) => ({ ...prev, list: dashboardData }));
      }
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    } finally {
      setDashboard((prev) => ({ ...prev, loading: false }));
    }
  }

  async function savePreferences() {
    try {
      const preferencesData = await API.graphql(
        graphqlOperation(userPreferencesByUserID, { userID: me.id })
      );
      const preferencesItems =
        preferencesData.data.userPreferencesByUserID.items ?? [];

      const filterParsed = {
        variables: filterBy.variables?.map((it) => it.shortName) ?? [],
        branches: filterBy.branches?.map((it) => it.id) ?? [],
        companies: filterBy.companies?.map((it) => it.id) ?? [],
        time: filterBy.time,
      };

      if (preferencesItems[0]) {
        await API.graphql(
          graphqlOperation(updateUserPreference, {
            input: {
              id: preferencesItems[0].id,
              dashboardStandard: JSON.stringify(filterParsed),
            },
          })
        );
      } else {
        await API.graphql(
          graphqlOperation(createUserPreference, {
            input: {
              userID: me.id,
              dashboardStandard: JSON.stringify(filterParsed),
            },
          })
        );
      }
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }

  async function fetchPreferences() {
    try {
      const variablesList = await fetchVariables();

      const preferencesData = await API.graphql(
        graphqlOperation(userPreferencesByUserID, { userID: me.id })
      );
      const preferencesItems =
        preferencesData.data.userPreferencesByUserID.items ?? [];

      if (preferencesItems[0]) {
        const dashboardObj = JSON.parse(
          preferencesItems[0].dashboardStandard ?? "[]"
        );

        if (dashboardObj.variables?.length > 0)
          dashboardObj.variables =
            variablesList?.filter((varLs) =>
              dashboardObj.variables?.includes(varLs.shortName)
            ) ?? [];
        if (dashboardObj.branches?.length > 0)
          dashboardObj.branches = await getBranchesByIds(dashboardObj.branches);
        if (dashboardObj.companies?.length > 0)
          dashboardObj.companies = await getCompaniesByIds(
            me.companyID ? [me.companyID] : dashboardObj.companies
          );

        setFilterBy({
          variables: dashboardObj.variables ?? [],
          branches: dashboardObj.branches ?? [],
          companies: dashboardObj.companies ?? [],
          time: dashboardObj.time,
        });
      } else if (me.companyID) {
        const companiesMapped = await getCompaniesByIds([me.companyID]);
        setFilterBy((prev) => ({
          ...prev,
          companies: companiesMapped,
        }));
      }
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    } finally {
      setFilterBy((prev) => ({
        ...prev,
        isLoaded: true,
      }));
    }
  }

  useEffect(() => {
    fetchPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (filterBy.isLoaded) {
      fetchDashboard();
      savePreferences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBy]);

  useEffect(() => {
    if (!!searchBy.company.id) onAdd("companies", searchBy.company.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.company.id]);

  useEffect(() => {
    if (!!searchBy.branch.id) onAdd("branches", searchBy.branch.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.branch.id]);

  return (
    <div>
      <div className="py-3">
        <>
          <Grid
            className="mb-3"
            rowGap="15px"
            columnGap="15px"
            alignItems="center"
            templateColumns={{ base: "1fr", xxl: "4fr 4fr 4fr 4fr 2fr" }}
          >
            <CompanyBranchFilter
              searchBy={searchBy}
              setSearchBy={setSearchBy}
            />
            {variables.loading ? (
              <Loader />
            ) : (
              <div style={styles.searchBy}>
                <SelectField
                  name="variableId"
                  value=""
                  onChange={(e) => onAdd("variables", e.target.value)}
                >
                  <option value="">
                    {strings.fields.variable.placeholder}
                  </option>
                  {variables.list
                    ?.filter(
                      (variable) =>
                        !filterBy.variables?.some(
                          (filVar) => filVar === variable.id
                        )
                    )
                    ?.map((variable) => (
                      <option value={variable.shortName}>
                        {variable.name}
                      </option>
                    ))}
                </SelectField>
              </div>
            )}
            <div style={styles.searchBy}>
              <SelectField
                name="time"
                value={filterBy.time}
                onChange={(e) => onSelectTime(e.target.value)}
              >
                <option value="">{strings.fields.time.placeholder}</option>
                {Object.entries(strings.fields.time.options).map((timeOp) => (
                  <option value={timeOp[0]}>{timeOp[1]}</option>
                ))}
              </SelectField>
            </div>
            <Button
              style={styles.buttonRefresh}
              variation="link"
              onClick={() => fetchDashboard()}
            >
              <AutorenewIcon /> {strings.refresh}
            </Button>
          </Grid>
          <Grid
            rowGap="15px"
            columnGap="15px"
            templateColumns={{ base: "1fr", xxl: "4fr 4fr 4fr 4fr 2fr" }}
            className="mb-3"
          >
            {companiesLoading && filterBy.companies?.length <= 0 && <Loader />}
            {filterBy.companies?.length > 0 && (
              <StandardFilterCard
                title={strings.cards.companies}
                list={filterBy.companies}
                loading={companiesLoading}
                onRemove={(id) => onRemove("companies", id)}
              />
            )}
            {branchesLoading && filterBy.branches?.length <= 0 && <Loader />}
            {filterBy.branches?.length > 0 && (
              <StandardFilterCard
                title={strings.cards.branches}
                list={filterBy.branches}
                loading={branchesLoading}
                onRemove={(id) => onRemove("branches", id)}
              />
            )}
            {filterBy.variables?.length > 0 && (
              <StandardFilterCard
                title={strings.cards.variables}
                list={filterBy.variables}
                loading={variables.loading}
                onRemove={(id) => onRemove("variables", id)}
              />
            )}
          </Grid>
          {dashboard.loading ? (
            <Loader />
          ) : (
            <CTable
              className="table-centered"
              items={dashboard.list}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
          )}
        </>
      </div>
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Tablero Estándar",
    responses: {
      errorFetch: "Error al consultar tablero",
      errorFetchVariables: "Error al consultar variables",
      errorFetchBranches: "Error al consultar sedes",
      errorFetchCompanies: "Error al consultar empresas",
    },
    table: {
      headers: {
        company: "Empresa",
        branch: "Sede",
        device: "Dispositivo",
      },
    },
    fields: {
      variable: {
        label: "Variable",
        placeholder: "Seleccione una variable",
      },
      time: {
        label: "Tiempo",
        placeholder: "Selecciona un tiempo",
        options: {
          lastValue: "Último valor",
          hour: "Última hora",
          day: "Último día",
          week: "Última semana",
          month: "Último mes",
        },
      },
    },
    cards: {
      companies: "Empresas",
      branches: "Sedes",
      variables: "Variables",
    },
    refresh: "Refrescar",
  },
});

const styles = {
  searchBy: {
    marginTop: "-10px",
  },
  buttonRefresh: {
    float: "right",
    marginBottom: "15px",
    width: "fit-content",
  },
};

export default DashboardStandard;
